import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { License } from '@classes/license';
import { Pagination } from '@classes/pagination/pagination';
import { PaginationRequest } from '@classes/pagination/pagination-request';
import {
  DatatableBaseComponent,
  columnParams,
} from '@components/datatable-base.component';
import { AuthService } from '@services/auth.service';
import { LicensesService } from '@services/licenses.service';
import { UiService } from '@services/ui.service';
import { Observable, Subscription } from 'rxjs';
import { licensesColumnsParams } from './licenses-columns-params';
import { PageEvent } from '@angular/material/paginator';
import { ClientsService } from '@services/clients.service';
import { Client } from '@classes/client';
import { UntypedFormBuilder } from '@angular/forms';
import { state, style, trigger } from '@angular/animations';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@components/confirm-dialog/confirm-dialog.component';
import { Router } from '@angular/router';
import { DocumentCategory } from '@classes/document/documentCategory';
import { UsersService } from '@services/users.service';

@Component({
  selector: 'app-licenses',
  templateUrl: './licenses.component.html',
  styleUrls: ['./licenses.component.scss'],
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', display: 'none' }),
      ),
      state('expanded', style({ height: '*' })),
    ]),
  ],
})
export class LicensesComponent
  extends DatatableBaseComponent
  implements OnInit
{
  // Constants
  private readonly DEFAULT_SORT_VALUE: string = '-startDate';
  private readonly PAGE_SIZE: number = 20;
  private readonly FILTERS_KEY: string = 'licenseFilters';

  // State
  public dataSource: MatTableDataSource<License>;
  public pagination: Pagination;
  public isLoadingFilterForm: boolean;
  private _paginationRequest: PaginationRequest;
  private _licenses: License[];

  // Filter
  public clients: Client[] = [];
  public filterForm = this._fb.group({
    clientName: [],
    range: this._fb.group({
      startDate: [],
      endDate: [],
    }),
  });

  constructor(
    protected uiSrv: UiService,
    protected _authSrv: AuthService,
    private _licensesSrv: LicensesService,
    private _clientsSrv: ClientsService,
    private _fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private _router: Router,
    private _userService: UsersService,
  ) {
    super(uiSrv, _authSrv);
    this.dataSource = new MatTableDataSource<License>();
  }

  public ngOnInit(): void {
    if (this._userService.globalSelectedClient) {
      this.filterForm
        .get('clientName')
        .setValue(this._userService.globalSelectedClient.name);
    }
    this._initializeSubscriptions();
    this._initColumnsParams();
    this._loadFilterDatas();

    this._readFilterFromSessionStorage();
    if (this._userService.globalSelectedClient) {
      this.filterForm.patchValue({
        clientName: this._userService.globalSelectedClient.name,
      });
    }

    this._paginationRequest = new PaginationRequest(
      this.filterForm.value,
      this._generateSortValue({}),
      1,
      this.PAGE_SIZE,
    );

    this._fetchLicenses();
  }

  private _initializeSubscriptions(): void {
    this.addSubscription(this._subscribeToLicensesSubjectChanges());
    this.addSubscription(this._subscribeToPaginationSubjectChanges());

    this.addSubscription(
      this._userService.globalSelectedClientSubject.subscribe((client) => {
        this.filterForm.get('clientName').setValue(client ? client.name : null);
        this.onFilter();
        this._fetchLicenses();
      }),
    );
  }

  private _subscribeToLicensesSubjectChanges(): Subscription {
    return this._licensesSrv.licensesSubject.subscribe((licenses: License[]) =>
      this._onLicensesSubjectChanges(licenses),
    );
  }

  private _onLicensesSubjectChanges(licenses: License[]): void {
    this._licenses = licenses;
    this.dataSource.data = this._licenses;
    this.isLoading = false;
  }

  private _subscribeToPaginationSubjectChanges(): Subscription {
    return this._licensesSrv.paginationSubject.subscribe(
      (pagination: Pagination) => this._onPaginationSubjectChanges(pagination),
    );
  }

  private _onPaginationSubjectChanges(pagination: Pagination): void {
    this.pagination = pagination;
  }

  private _fetchLicenses(): void {
    this.isLoading = true;
    this._licensesSrv.fetch(this._paginationRequest);
  }

  private _initColumnsParams() {
    this.localStorageColumnsParamsName = 'ColumnsParamsLicenses';
    this.columnsParams = licensesColumnsParams;
    this.readColumnsParamsFromLocalStorage();
    this.generateDisplayedColumns();
  }

  private _loadFilterDatas(): void {
    this.isLoadingFilterForm = true;

    const promises = [this._loadClients()];

    Promise.all(promises).then(() => {
      this.isLoadingFilterForm = false;
    });
  }

  private _loadClients(): Promise<void> {
    return new Promise((resolve) => {
      this._clientsSrv.getList().subscribe((clients: Client[]) => {
        this.clients = clients;
        resolve();
      });
    });
  }

  public findColumnTranslationKey(name: string): string {
    const columnParam = this._findColumnParam(name);
    return columnParam ? columnParam.title : '';
  }

  private _findColumnParam(name: string): columnParams {
    return this.columnsParams.find((columnParam) => columnParam.name === name);
  }

  public onPageChanged(pageEvent: PageEvent): void {
    this._paginationRequest.page = pageEvent.pageIndex + 1;
    this._fetchLicenses();
  }

  public onSortChanged(sortEvent): void {
    this._paginationRequest.sort = this._generateSortValue(sortEvent);
    this._resetPagination();
    this._fetchLicenses();
  }

  private _generateSortValue({ direction, active }: any): string {
    let sortValue = '';

    if (direction) {
      sortValue = `${direction === 'asc' ? '' : '-'}${active}`;
    } else {
      sortValue = this.DEFAULT_SORT_VALUE;
    }

    return sortValue;
  }

  public onFilter(event?): void {
    const formValue = this.filterForm.value;
    this._paginationRequest.query = formValue;
    this._resetPagination();
    this._saveFilterInSessionStorage(formValue);
    this._fetchLicenses();
  }

  private _saveFilterInSessionStorage(filter: any): void {
    sessionStorage.setItem(this.FILTERS_KEY, JSON.stringify(filter));
  }

  private _readFilterFromSessionStorage(): void {
    if (sessionStorage.getItem(this.FILTERS_KEY)) {
      const filter = JSON.parse(sessionStorage.getItem(this.FILTERS_KEY));
      this.filterForm.patchValue(filter);
    }
  }

  private _resetPagination(): void {
    this._paginationRequest.page = 1;
  }

  public clearFilters(): void {
    this.filterForm.reset();
    this._resetPagination();
    this._saveFilterInSessionStorage({});
    this._fetchLicenses();
  }

  public async onDelete(license: License): Promise<void> {
    const confirmation = await this._confirmDeleteDialog();

    if (confirmation) {
      this._deleteLicense$(license).subscribe(() => {
        this._displayDeletedNotification();
      });
    }
  }

  private _confirmDeleteDialog(): Promise<boolean> {
    return new Promise((resolve) => {
      const dialog = this._openDeleteConfirmationDialog();

      dialog.afterClosed().subscribe((response) => {
        resolve(response || false);
      });
    });
  }

  private _openDeleteConfirmationDialog(): MatDialogRef<
    ConfirmDialogComponent,
    any
  > {
    return this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '500px',
      data: {
        title: 'repository.licenses.delete.confirmDialog.title',
        content: 'repository.licenses.delete.confirmDialog.content',
      },
    });
  }

  private _deleteLicense$(license: License): Observable<License> {
    return this._licensesSrv.delete$(license);
  }

  private _displayDeletedNotification(): void {
    this.uiSrv.showSnackbar(
      'repository.licenses.delete.deletedNotification',
      true,
    );
  }

  public onGoToDocuments(license: License) {
    this._router.navigate([
      `documents?category=${DocumentCategory.invoice}&client=${license.client.name}`,
    ]);
  }
}
